import {
  inject,
  Injectable,
  signal,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

/**
 * This service overrides the active state management to only return the lowest active item as being active.
 * It's a last-minute hack before going prod. A cleaner approach would involve creating a directive that extends the Angular RouterLinkActive directive.
 */
@Injectable({ providedIn: 'root' })
export class ActiveRouteService {
  private readonly _router = inject(Router);

  private readonly _paths = new Map<
    string,
    {
      active: boolean;
      matchedPath: string;
      isTheClosestActive: WritableSignal<boolean>;
    }
  >();

  /**
   * Seeded with the router's current url: the first navigation can land before
   * the first menu item builds this service, and without it every item would
   * stay unranked until the user navigates again.
   */
  private readonly _currentPath = toSignal(
    this._router.events.pipe(
      takeUntilDestroyed(),
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
    ),
    { initialValue: this._router.url },
  );

  public isTheClosestRouteActive(path: string): Signal<boolean> {
    this._register(path);

    return this._paths.get(path).isTheClosestActive;
  }

  /**
   * `matchedPath` is the route the item is active *on*, which is its own path
   * unless it was matched through one it merely owns. Ranking reads it, so an
   * entry pointing at a sister route still outranks its parent.
   */
  public setRouteActiveState(
    path: string,
    isActive: boolean,
    matchedPath: string = path,
  ): void {
    this._register(path);

    this._paths.get(path).active = isActive;
    this._paths.get(path).matchedPath = matchedPath;
    this._paths.get(path).isTheClosestActive.set(isActive);

    this._setOnlyTheClosestRouteActive();
  }

  public removeRoute(path: string): void {
    this._paths.delete(path);
    this._setOnlyTheClosestRouteActive();
  }

  private _register(path: string): void {
    if (this._paths.has(path)) {
      return;
    }

    this._paths.set(path, {
      active: null,
      matchedPath: path,
      isTheClosestActive: signal(null),
    });
  }

  private _setOnlyTheClosestRouteActive(): void {
    let closestPath = '';

    let closestPathLength = 0;

    if (!this._currentPath()) {
      return;
    }

    for (const [path, { matchedPath }] of Array.from(this._paths).filter(
      ([_, { active }]) => active === true,
    )) {
      if (!path) {
        continue;
      }

      const pathSegments = matchedPath.split('/').filter(Boolean);
      const currentPathSegments = this._currentPath()
        .split('/')
        .filter(Boolean);

      const commonSegmentsLength = pathSegments.filter(segment =>
        currentPathSegments.includes(segment),
      ).length;

      if (commonSegmentsLength && commonSegmentsLength > closestPathLength) {
        closestPath = path;
        closestPathLength = commonSegmentsLength;
      }
    }
    this._paths.forEach(({ isTheClosestActive }, path) => {
      isTheClosestActive.set(path === closestPath);
    });
  }
}
